import React, { useState, useEffect } from 'react';
import { TimerDisplay } from './TimerDisplay';
import { TimerControls } from './TimerControls';
import { useTimerDisplay } from '../../hooks/useTimer';
import { getServerTime } from '../../utils/serverTime';
import type { Timer } from '../../types';

// Seuil d'alerte avant avertissement si un jeu tourne sans chrono (2 minutes)
export const UNTRACKED_PLAY_THRESHOLD_MS = 2 * 60 * 1000;

interface TimerCardProps {
  timer: Timer;
  timerId: string;
  onStart: (id: string) => void;
  onPause: (id: string, accumulatedMs: number) => void;
  onStop: (id: string, timer: Timer, totalMs: number) => void;
  onReset: (id: string, elapsedMs?: number) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onToggleFreePlay?: (id: string, enabled: boolean) => void;
}

export function TimerCard({
  timer,
  timerId,
  onStart,
  onPause,
  onStop,
  onReset,
  onRename,
  onDelete,
  onToggleFreePlay,
}: TimerCardProps) {
  const displayMs = useTimerDisplay(timer);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(timer.name);
  const [now, setNow] = useState(() => getServerTime());

  // Horloge de rafraîchissement si un jeu est actif hors-chrono
  useEffect(() => {
    if (!timer.activeGame || timer.state === 'running' || timer.freePlayMode) return;
    const interval = setInterval(() => {
      setNow(getServerTime());
    }, 2000);
    return () => clearInterval(interval);
  }, [timer.activeGame, timer.state, timer.freePlayMode]);

  const handleSaveName = () => {
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== timer.name) {
      onRename(timerId, trimmed);
    }
    setIsEditingName(false);
  };

  const computeAccumulated = (): number => {
    if (timer.state === 'running' && timer.startedAt !== null) {
      return (timer.accumulatedMs || 0) + (getServerTime() - timer.startedAt);
    }
    return timer.accumulatedMs || 0;
  };

  const isRunning = timer.state === 'running';

  // Alerte visuelle 1 : compte à rebours terminé en cours de fonctionnement
  const isExpired = timer.type === 'countdown' && isRunning && displayMs <= 0;

  // Alerte visuelle 2 : jeu lancé sans chrono actif (au-delà de 3 min), sauf en mode Roue Libre
  const untrackedElapsedMs = (timer.activeGame && timer.activeGame.detectedAt && timer.state !== 'running' && !timer.freePlayMode)
    ? Math.max(0, now - timer.activeGame.detectedAt)
    : 0;
  const isUntrackedPlay = untrackedElapsedMs >= UNTRACKED_PLAY_THRESHOLD_MS;
  const untrackedMinutes = Math.floor(untrackedElapsedMs / 60000);

  // Statut de connexion de l'agent PC
  const isAgentOnline = Boolean(
    timer.agentOnline ||
    (timer.lastAgentPing && (now - timer.lastAgentPing) < 45000) ||
    timer.activeGame
  );

  return (
    <div
      className={`
        relative border p-5 transition-all duration-300
        ${isExpired
          ? 'border-namm-danger bg-gradient-to-b from-namm-danger/20 via-namm-panel/95 to-namm-panel shadow-[0_0_30px_rgba(255,59,59,0.3)] animate-pulse'
          : isUntrackedPlay
          ? 'border-amber-500 bg-gradient-to-b from-amber-500/15 via-namm-panel/95 to-namm-panel shadow-[0_0_25px_rgba(245,158,11,0.25)] animate-pulse'
          : timer.freePlayMode
          ? 'border-violet-500/50 bg-gradient-to-b from-violet-500/10 via-namm-panel/85 to-namm-panel shadow-[0_0_20px_rgba(124,58,237,0.15)]'
          : isRunning
          ? 'border-namm-cyan/50 bg-namm-panel/70 shadow-[0_0_24px_rgba(0,229,255,0.1)]'
          : 'border-namm-line/40 bg-namm-panel/70 hover:border-namm-line/70'
        }
      `}
    >
      {/* Accent line top right */}
      <div
        className={`absolute top-3 right-3 w-8 h-px transition-colors duration-300 ${
          isExpired
            ? 'bg-namm-danger shadow-[0_0_8px_rgba(255,59,59,0.8)]'
            : isUntrackedPlay
            ? 'bg-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.8)]'
            : timer.freePlayMode
            ? 'bg-violet-400 shadow-[0_0_8px_rgba(124,58,237,0.8)]'
            : 'bg-namm-cyan'
        }`}
      />

      {/* Type badge / Alert status */}
      <div className="flex items-center justify-between mb-4">
        {isExpired ? (
          <span className="text-[10px] font-mono uppercase tracking-widest text-namm-danger font-bold flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-namm-danger animate-ping inline-block" />
            🚨 Temps Écoulé
          </span>
        ) : isUntrackedPlay ? (
          <span className="text-[10px] font-mono uppercase tracking-widest text-amber-400 font-bold flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping inline-block" />
            ⚠️ Jeu sans chrono ({untrackedMinutes}m)
          </span>
        ) : timer.freePlayMode ? (
          <span className="text-[10px] font-mono uppercase tracking-widest text-violet-400 font-bold flex items-center gap-1.5">
            🕊️ Roue Libre (Staff)
          </span>
        ) : (
          <span className="text-[10px] font-mono uppercase tracking-widest text-namm-cyan">
            {timer.type === 'stopwatch' ? '⏱ Chrono' : '⏳ Countdown'}
          </span>
        )}

        <div className="flex items-center gap-2">
          {onToggleFreePlay && (
            <button
              onClick={() => onToggleFreePlay(timerId, !timer.freePlayMode)}
              className={`px-1.5 py-0.5 text-[9px] font-mono rounded border transition-all ${
                timer.freePlayMode
                  ? 'border-violet-500 bg-violet-500/20 text-violet-300 font-bold'
                  : 'border-namm-line/30 text-namm-muted hover:border-violet-500/50 hover:text-violet-300'
              }`}
              title={
                timer.freePlayMode
                  ? 'Désactiver le mode Roue Libre'
                  : 'Activer le mode Roue Libre (Bénévole / Non surveillé)'
              }
            >
              🕊️ {timer.freePlayMode ? 'Roue Libre' : 'Bénévole'}
            </button>
          )}
          <button
            onClick={() => onDelete(timerId)}
            className="text-namm-muted hover:text-namm-danger transition-colors text-xs"
            title="Supprimer"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Timer name */}
      <div className="mb-3">
        {isEditingName ? (
          <input
            type="text"
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={handleSaveName}
            onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
            className="bg-transparent border-b border-namm-cyan text-namm-text font-heading text-lg uppercase tracking-tight outline-none w-full"
            autoFocus
          />
        ) : (
          <h3
            onClick={() => {
              setNameValue(timer.name);
              setIsEditingName(true);
            }}
            className={`font-heading text-lg uppercase tracking-tight cursor-pointer transition-colors ${
              isExpired
                ? 'text-namm-danger font-bold'
                : isUntrackedPlay
                ? 'text-amber-400 font-bold'
                : 'text-namm-text hover:text-namm-cyan'
            }`}
          >
            {timer.name}
          </h3>
        )}
      </div>

      {/* Statut du monitoring PC / namm-agent */}
      {timer.activeGame ? (
        <div
          className={`mb-4 py-1 px-2.5 border flex items-center justify-between gap-2 text-xs transition-colors ${
            isUntrackedPlay
              ? 'bg-amber-500/10 border-amber-500/40'
              : timer.freePlayMode
              ? 'bg-violet-500/10 border-violet-500/30'
              : 'bg-namm-panel-soft border-namm-cyan/30'
          }`}
        >
          <div className="flex items-center gap-2 truncate">
            <span
              className={`w-2 h-2 rounded-full shrink-0 ${
                isUntrackedPlay
                  ? 'bg-amber-400 animate-ping'
                  : timer.freePlayMode
                  ? 'bg-violet-400'
                  : 'bg-namm-cyan animate-pulse'
              }`}
            />
            <span className="text-namm-text font-semibold truncate text-[11px]">
              {timer.activeGame.friendlyName}
            </span>
          </div>
          <span
            className={`text-[9px] uppercase tracking-wider shrink-0 font-mono ${
              isUntrackedPlay
                ? 'text-amber-400 font-bold'
                : timer.freePlayMode
                ? 'text-violet-400'
                : 'text-namm-cyan'
            }`}
          >
            {isUntrackedPlay
              ? `⚠️ Sans chrono (${untrackedMinutes}m)`
              : timer.freePlayMode
              ? '🕊️ Roue libre'
              : '🎮 Live'}
          </span>
        </div>
      ) : isAgentOnline ? (
        <div className="mb-4 py-1 px-2.5 border border-emerald-500/30 bg-emerald-500/5 flex items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-2 truncate">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
            <span className="text-namm-muted text-[10px] truncate">
              Agent connecté{timer.agentPcName ? ` (${timer.agentPcName})` : ''} · Bureau
            </span>
          </div>
          <span className="text-[9px] uppercase tracking-wider text-emerald-400 font-mono">
            🟢 Prêt
          </span>
        </div>
      ) : (
        <div className="mb-4 py-1 px-2.5 border border-dashed border-namm-line/20 bg-namm-panel-soft/30 flex items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-2 truncate">
            <span className="w-1.5 h-1.5 rounded-full bg-namm-line/50 shrink-0" />
            <span className="text-namm-muted/70 text-[10px] truncate">
              Aucun script PC associé
            </span>
          </div>
          <span className="text-[9px] uppercase tracking-wider text-namm-muted/50 font-mono">
            Manuel
          </span>
        </div>
      )}

      {/* Display */}
      <div className="mb-5">
        <TimerDisplay displayMs={displayMs} state={timer.state} type={timer.type} />
      </div>

      {/* Controls */}
      <TimerControls
        state={timer.state}
        onStart={() => onStart(timerId)}
        onPause={() => onPause(timerId, computeAccumulated())}
        onStop={() => onStop(timerId, timer, computeAccumulated())}
        onReset={() => onReset(timerId, computeAccumulated())}
      />

      {/* Last action info */}
      <div className="mt-3 text-[10px] font-mono text-namm-muted text-center uppercase tracking-widest">
        {timer.lastUpdatedBy && timer.state !== 'idle' && (
          <span>par {timer.lastUpdatedBy}</span>
        )}
      </div>
    </div>
  );
}
